/**
 * The one way the app talks to the API. Same origin, JSON, the session cookie travels on
 * its own. Every non-2xx answer becomes an ApiError carrying the ProblemDetails the API
 * writes, so a screen can show the API's own title and detail.
 */

import { ApiError, type ProblemDetails } from '@/lib/apiError';
import { uploadWithProgress } from '@/lib/upload';

export { ApiError, type ProblemDetails };

/**
 * Called when a request that expected a session gets a 401. Set once by main.tsx to send
 * the person back to the login screen as "session expired"; requests made by the login
 * screen itself pass `expectsSession: false` so a wrong password does not trigger it.
 */
let onUnauthorized: ((problem: ProblemDetails) => void) | undefined;
export function setUnauthorizedHandler(handler: (problem: ProblemDetails) => void) {
    onUnauthorized = handler;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
    body?: unknown;
    expectsSession?: boolean;
}

export async function api<T = void>(path: string, options: RequestOptions = {}): Promise<T> {
    const { body, expectsSession = true, headers, ...init } = options;

    // A FormData body is a file upload, and goes by XHR rather than fetch: an upload refused at
    // the edge never settles a fetch, and only XHR reports the progress that tells a stalled
    // upload from a slow one (uploadWithProgress, ALS-332). The browser writes the boundary.
    if (body instanceof FormData) {
        try {
            return await uploadWithProgress<T>(init.method ?? 'POST', path, body, {
                signal: init.signal ?? undefined,
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 401 && expectsSession) {
                onUnauthorized?.(error.problem);
            }
            throw error;
        }
    }

    const response = await fetch(path, {
        ...init,
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
            ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
        const problem = await readProblem(response);
        if (response.status === 401 && expectsSession) onUnauthorized?.(problem);
        throw new ApiError(response.status, problem);
    }

    // 204 never has a body. 202 usually has none (a reset request, an invite), but one that
    // reports a job it queued (ALS-83's PDF split) carries the job, so read it when it is JSON.
    if (response.status === 204) return undefined as T;
    if (response.status === 202 && !response.headers.get('Content-Type')?.includes('json')) {
        return undefined as T;
    }
    const data: unknown = await response.json();
    return data as T;
}

async function readProblem(response: Response): Promise<ProblemDetails> {
    try {
        const parsed: unknown = await response.json();
        if (parsed && typeof parsed === 'object') return parsed;
    } catch {
        // not JSON: fall through to the status line
    }
    return { status: response.status, title: response.statusText || undefined };
}
